import React from 'react';
import styled from 'styled-components';
import { BasicContainer, FlexDynamic, FlexRow, GridDynamic, H2Text, Link, PrimaryButton } from '@minecraft.atoms';
import { WindowWidthContext } from '@minecraft.utils';
import Select from '@minecraft.select';
import Button from '@minecraft.button';
import { DropdownButton, DropdownButtonProps } from './DropdownButton';

type BaseButtonConfig = {
  key: string;
  qaId?: string;
  testId?: string;
};

export const BUTTON_TYPES = {
  STANDARD: 'standard',
  DROPDOWN: 'dropdown',
  SELECT: 'select',
  LINK: 'link',
} as const;

export type StandardButtonConfig = {
  type: typeof BUTTON_TYPES.STANDARD;
  label: string;
  handleClick: () => void;
  iconName?: string;
  isSecondary?: boolean;
} & BaseButtonConfig;

export type DropdownButtonConfig = {
  type: typeof BUTTON_TYPES.DROPDOWN;
  label: string;
  menuItems: DropdownButtonProps['menuItemsProps'];
} & BaseButtonConfig;

export type SelectInputConfig = {
  type: typeof BUTTON_TYPES.SELECT;
  label: string;
  options: { value: string; label: string }[];
  onChange: (newVal: string) => void;
  value: string;
} & BaseButtonConfig;

export type LinkButtonConfig = {
  type: typeof BUTTON_TYPES.LINK;
  label: string;
  to: string;
} & BaseButtonConfig;

export type HeaderButtonProps = StandardButtonConfig | DropdownButtonConfig | SelectInputConfig | LinkButtonConfig;

export type TalentRosterSubnavPages = 'talent' | 'history' | 'roster-lists';
export type TalentScoutSubnavPages = 'talent-scout' | 'scout-history';
export type AccountSettingsSubnavPages = 'account-details' | 'notification-settings' | 'administrative';

type SubnavPages = TalentRosterSubnavPages | TalentScoutSubnavPages | AccountSettingsSubnavPages;

export type SubnavItem = {
  label: string;
  to: string;
  page: SubnavPages;
};

export type SubnavProps = {
  items: SubnavItem[];
  currentPage: SubnavPages;
};

type SubnavItemProps = {
  isActive: boolean;
  label: string;
  testId?: string;
  to: string;
};

const StyledButton = styled(Button)<{ $noPadding?: boolean }>`
  color: ${(props): string => props.theme.colors.primary.primary};
  text-decoration: none;
  ${(props): string =>
    props.$noPadding
      ? `
      padding: 0;
      min-width: auto;
      height: auto;
      font-size: 1rem;
  `
      : ''}
`;

type TitleWrapperProps = {
  editButtonLabel?: string;
  title: string;
  onEditClick?: () => void;
};

const TitleWrapper: React.FC<TitleWrapperProps> = ({ editButtonLabel, title, onEditClick }) => {
  return (
    <FlexDynamic
      borderPlacement="bottom"
      w="full"
      mb="4"
      gap="2"
      alignItems="center"
      className="cn_atom_py-2 cn_atom_px-4"
      smProps={{ borderPlacement: 'none', mb: '0', w: 'auto', className: '' }}
    >
      <H2Text fontWeight="medium">{title}</H2Text>
      {editButtonLabel && (
        <StyledButton
          data-qa-id={`edit-${title}`}
          data-testid={`edit-${title}`}
          $noPadding
          color="link"
          onClick={onEditClick}
        >
          {editButtonLabel}
        </StyledButton>
      )}
    </FlexDynamic>
  );
};

const HeaderWrapper = ({ children }) => {
  return (
    <FlexDynamic
      borderPlacement="none"
      className="cn_h-auto cn_atom_py-2"
      alignItems="center"
      smProps={{
        className: 'cn_h-header cn_atom_px-8',
        borderPlacement: 'bottom',
      }}
    >
      {children}
    </FlexDynamic>
  );
};

type SubnavWrapperProps = {
  children: React.ReactNode;
  sectionName: string;
};

const SubnavWrapper = ({ children, sectionName }: SubnavWrapperProps) => {
  return (
    <FlexDynamic
      className="cn_atom_px-4"
      borderPlacement="bottom"
      data-testid={`${sectionName}-subnav-wrapper`}
      smProps={{ className: 'cn_h-header-subnav cn_atom_px-8' }}
    >
      {children}
    </FlexDynamic>
  );
};

const HeaderTitleButton = ({ config }: { config: HeaderButtonProps }) => {
  const isMobile = WindowWidthContext.useIsMobile();

  if (config.type === BUTTON_TYPES.STANDARD) {
    return (
      <PrimaryButton
        {...{
          $isTall: !isMobile,
          block: !isMobile,
          size: isMobile ? 'large' : 'small',
        }}
        key={config.key}
        onClick={config.handleClick}
        startIcon={config.iconName}
        data-qa-id={config.qaId}
        data-testid={config.testId}
        variant={config.isSecondary ? 'secondary' : 'solid'}
        label={config.label}
      />
    );
  }

  if (config.type === BUTTON_TYPES.DROPDOWN) {
    return (
      <DropdownButton
        key={config.key}
        toggleLabel={config.label}
        mobileBlock
        toggleProps={{ mobileBlock: true }}
        qaId={config.qaId}
        testId={config.testId}
        menuItemsProps={config.menuItems}
      />
    );
  }

  if (config.type === BUTTON_TYPES.SELECT) {
    return (
      <Select
        showLabel
        id={config.label}
        label={config.label}
        options={config.options}
        value={config.value}
        onChange={(e) => config.onChange(e.target.value)}
        direction={isMobile ? 'horizontal' : 'vertical'}
        wrapperProps={{ className: isMobile ? undefined : 'cn_w-full' }}
      />
    );
  }

  if (config.type === BUTTON_TYPES.LINK) {
    return <Link to={config.to}>{config.label}</Link>;
  }

  return null;
};

const SubnavItem = ({ isActive, label, testId, to }: SubnavItemProps) => {
  return (
    <FlexDynamic
      borderPlacement={isActive ? 'bottom' : 'none'}
      borderSize={0.15}
      data-testid={`${label}-nav-item`}
      borderColor="emphasis"
      justifyItems="center"
      alignItems="center"
      className="cn_h-header-subnav"
    >
      <Link to={to} aria-label={label} key={label} data-testid={testId} disabled={isActive}>
        {label}
      </Link>
    </FlexDynamic>
  );
};

const Subnav = ({ items, currentPage, sectionName }: SubnavProps & { sectionName: string }) => {
  return (
    <FlexRow data-testid={`${sectionName}-subnav`} wrap="nowrap" gap="4" alignItems="flex-start" grow={0}>
      {items
        .filter((each) => {
          return !(!each?.label || !each?.page || !each?.to);
        })
        .map((item) => (
          <SubnavItem
            key={item.to}
            testId={`${item.label}-submenu`}
            isActive={currentPage === item.page}
            label={item.label}
            to={item.to}
          />
        ))}
    </FlexRow>
  );
};

export type PageHeaderProps = {
  title?: string;
  sectionName: string;
  subnav?: SubnavProps;
  buttons?: HeaderButtonProps[];
  editButtonLabel?: string;
  onEditClick?: () => void;
};

export const PageHeader = ({ buttons, sectionName, subnav, title, onEditClick, editButtonLabel }: PageHeaderProps) => {
  const isMobile = WindowWidthContext.useIsMobile();

  return (
    <GridDynamic data-testid={`${sectionName}-page-header`}>
      {subnav && (
        <SubnavWrapper sectionName={sectionName}>
          <Subnav sectionName={sectionName} {...subnav} />
        </SubnavWrapper>
      )}
      <HeaderWrapper>
        <FlexRow alignItems="center" justifyContent="space-between">
          <FlexRow gap="2" alignItems="center">
            {title && <TitleWrapper title={title} editButtonLabel={editButtonLabel} onEditClick={onEditClick} />}
          </FlexRow>
          <BasicContainer className={isMobile ? 'cn_w-full cn_atom_px-4' : undefined}>
            <FlexRow gap="2" alignItems="center">
              {buttons
                ?.filter((button) => {
                  return !(!button?.label || !button?.type);
                })
                .map((button) => (
                  <HeaderTitleButton key={button?.key} config={button} />
                ))}
            </FlexRow>
          </BasicContainer>
        </FlexRow>
      </HeaderWrapper>
    </GridDynamic>
  );
};
